import { ApostleLite } from '@apeiron/apostle'
import { Planet } from '@apeiron/planet'
import { MayNull, NextImage, noForwardProps } from '@apeiron/temp'
import { ApeironTooltip } from '@apeiron/ui'
import { Box, styled } from '@mui/material'
import ApostleCard from '@src/components/apostles/card/ApostleCard'
import { ApostleSkillsProvider } from '@src/contexts/apostle/ApostleSkillsContext'
import * as R from 'ramda'
import * as RA from 'ramda-adjunct'
import { FC, useMemo } from 'react'

const Container = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
`

const Square = styled(Box, noForwardProps)<SquareProps>`
  align-items: center;
  aspect-ratio: 1;
  background-clip: content-box, border-box;
  background-image: linear-gradient(#161819, #161819),
    linear-gradient(#d2d4d6, #80807e, #d8dadc, #41474c, #9ea0a2);
  background-origin: border-box;
  border-radius: 2px;
  border: 2px solid transparent;
  box-shadow: 0px 0px 4px #000000aa;
  display: flex;
  justify-content: center;
  padding: unset;
  width: 50px;

  ${props =>
    props.__dim
      ? 'border: 1px solid rgba(0, 0, 0, 0.6);'
      : 'border: 2px solid transparent;'}
`

const Slot = styled(NextImage)`
  width: 100%;
  height: 100%;
`

const Tooltip = styled(ApeironTooltip)`
  background-color: red;
`

const PlanetApostleSlots: FC<Props> = props => {
  const { className, planet } = props

  const slots = useMemo((): MayNull<ApostleLite>[] => {
    const apostleSlots = R.pluck(
      'apostle',
      planet.relicApostles
    ) as MayNull<ApostleLite>[]

    const emptySlots = R.repeat(
      null,
      planet.relicApostleLimit - R.length(apostleSlots)
    )

    return R.concat(apostleSlots, emptySlots)
  }, [planet.relicApostleLimit, planet.relicApostles])

  return (
    <ApostleSkillsProvider>
      <Container className={className}>
        {RA.mapIndexed((slot: MayNull<ApostleLite>, index: number) => {
          const isFilled = R.isNotNil(slot)

          const key = isFilled ? slot.id : `slot-${index}`

          return isFilled ? (
            <Square data-tooltip-id={key} key={key}>
              <Slot src={slot.image} />
              {R.isNotNil(slot) && (
                <Tooltip
                  style={{
                    backgroundColor: '#ffffff11',
                    borderRadius: '8px'
                  }}
                  id={key}
                >
                  <ApostleCard apostle={slot} />
                </Tooltip>
              )}
            </Square>
          ) : (
            <Square __dim key={key} />
          )
        }, slots)}
      </Container>
    </ApostleSkillsProvider>
  )
}

type SquareProps = {
  __dim?: boolean
}

type Props = {
  className?: string
  planet: Planet
}

export default PlanetApostleSlots
